import { circleVsCircle, segmentVsAabbs } from '../engine/collision.js';
import { animateEnemyHitExplosion2D } from './ui/animator.js';

function recordPerfSection(game, sectionName, deltaMs) {
  const section = game?._perf?.sections?.[sectionName];
  if (!section) return;
  section.ms += deltaMs;
  section.calls += 1;
}

function profilePerfSection(game, sectionName, fn) {
  const t0 = performance.now();
  const result = fn();
  recordPerfSection(game, sectionName, performance.now() - t0);
  return result;
}

export function updateProjectiles(game, dt) {
  game.projectiles.forEach((p) => {
    if (!p.alive) return;
    p.life -= dt;
    p.age += dt;
    if (p.life <= 0) { p.mesh.visible = false; game.projectiles.release(p); return; }

    const moveX = p.dx * p.speed * dt;
    const moveZ = p.dz * p.speed * dt;
    const nx = p.x + moveX;
    const nz = p.z + moveZ;

    // Wall collision (segment test)
    const t = segmentVsAabbs(p.x, p.z, nx, nz, game.room.walls);
    if (t !== null) {
      p.x = p.x + moveX * t;
      p.z = p.z + moveZ * t;
      if (p.team === 0 && p.explosionRadius > 0) {
        game._explodePlayerProjectile(p, p.x, p.z);
      }
      p.mesh.visible = false;
      game.projectiles.release(p);
      return;
    }

    p.x = nx;
    p.z = nz;
    p.mesh.position.set(p.x, 1.05, p.z);
    p.mesh.rotation.y = Math.atan2(p.dx, p.dz);
    // Dim bullets that are outside the player's light radius.
    const illum = game.illuminationAt(p.x, p.z);
    p.mesh.material.color.copy(p.baseColor).multiplyScalar(illum);
    p.mesh.material.opacity = p.baseOpacity * (0.55 + 0.45 * illum);
    const glowBoost = p.team === 0 ? 1.16 : 1.03;
    p.trail.material.color.copy(p.baseColor).multiplyScalar(illum * glowBoost);
    p.glow.material.color.copy(p.baseColor).multiplyScalar(illum * glowBoost * 1.08);
    p.outerGlow.material.color.copy(p.baseColor).multiplyScalar(illum * glowBoost * 1.18);
    p.trail.material.opacity = p.trailOpacity * (0.38 + 0.62 * illum);
    p.glow.material.opacity = p.glowOpacity * (0.45 + 0.55 * illum);
    p.outerGlow.material.opacity = p.outerGlowOpacity * (0.3 + 0.7 * illum);

    // Spawn animation: brief overshoot/spring on projectile length + glow pulse.
    const overshootWindow = 0.17;
    let stretch = 1;
    if (p.age < overshootWindow) {
      const overshootT = p.age / overshootWindow;
      stretch = 1 + Math.sin(overshootT * Math.PI) * 0.46 * (1 - overshootT * 0.32);
    }
    const glowPulse = 1 + Math.sin(p.age * 38) * 0.1;
    const outerGlowPulse = 1 + Math.sin(p.age * 13 + 0.4) * 0.16;
    p.mesh.scale.set(1, 1, stretch);
    p.trail.scale.set(1, 1, 0.9 + stretch * 0.65);
    p.glow.scale.setScalar((0.95 + stretch * 0.1) * glowPulse);
    p.outerGlow.scale.setScalar((1.1 + stretch * 0.2) * outerGlowPulse);

    // Hit tests
    if (p.team === 0) {
      if (p.delayedFuse > 0) {
        p.delayedFuse -= dt;
        if (p.delayedFuse <= 0 && p.explosionRadius > 0) {
          game._explodePlayerProjectile(p, p.x, p.z);
          p.mesh.visible = false;
          game.projectiles.release(p);
          return;
        }
      }
      for (const e of game.enemies) {
        if (!e.alive) continue;
        if (circleVsCircle(p.x, p.z, 0.2, e.x, e.z, e.radius)) {
          game.audio?.play('enemyHit', { rate: 0.9 + Math.random() * 0.2 });
          e.queueHitDirection?.(p.dx, p.dz);
          e.takeDamage(p.damage, game);
          game.onPlayerHitEnemy(e, p);
          const hitScreen = game._worldToScreen(e.x, 1.05, e.z);
          if (hitScreen) {
            animateEnemyHitExplosion2D(hitScreen.x, hitScreen.y, {
              emitterSize: 36,
              dotQuantity: 18,
              dotSizeMin: 2,
              dotSizeMax: 5,
              speed: 0.78,
              gravity: 0.85,
            });
          }
          game.alertNearbyEnemies(e.x, e.z);
          if (p.bleedDps > 0) {
            e._bleedDps = p.bleedDps;
            e._bleedTime = Math.max(e._bleedTime || 0, p.bleedDuration || 0);
            e._bleedTick = 0;
          }
          if ((p.maxBounces || 0) > 0 && (p.bounceCount || 0) < p.maxBounces) {
            // find nearest other enemy within 6m
            let best = null; let bestD = 36;
            for (const o of game.enemies) {
              if (!o.alive || o === e) continue;
              const d = Math.hypot(o.x - p.x, o.z - p.z);
              if (d * d < bestD) { bestD = d * d; best = o; }
            }
            if (best) {
              const dx = best.x - p.x;
              const dz = best.z - p.z;
              const l = Math.hypot(dx, dz) || 1;
              p.dx = dx / l;
              p.dz = dz / l;
              p.bounceCount = (p.bounceCount || 0) + 1;
              p.life = Math.max(p.life, 0.4);
              return;
            }
          }
          if (p.explosionRadius > 0) game._explodePlayerProjectile(p, p.x, p.z);
          p.mesh.visible = false;
          game.projectiles.release(p);
          return;
        }
      }
    } else if (game.player.alive && game.player.invuln <= 0 &&
        circleVsCircle(p.x, p.z, 0.2, game.player.x, game.player.z, game.player.radius)) {
      game.player.takeDamage(p.damage, game);
      p.mesh.visible = false;
      game.projectiles.release(p);
    }
  });
}

export function updatePlayingFrame(game, dt) {
  const ENEMY_FAR_LOD_DIST = 30;
  const ENEMY_FAR_LOD_DIST_SQ = ENEMY_FAR_LOD_DIST * ENEMY_FAR_LOD_DIST;
  if (game.input.consumeKey('KeyO')) {
    game._triggerEnemyDropPreviewAtPlayer();
  }
  if (game.runMode !== 'sandbox' && game.input.consumeMedkitPressed()) {
    const usedMedkit = game.player.useMedkit();
    if (usedMedkit) game.hud?.playMedkitUseFx?.();
  }
  if (game._timeScaleTimer > 0) {
    game._timeScaleTimer -= dt;
    if (game._timeScaleTimer <= 0) game.timeScale = 1;
  }
  const sdt = dt * game.timeScale;
  game._syncSlashFxDebug();

  game._updateBurstShots(sdt);
  game.player.update(sdt, game.input, game.room.walls, game);
  game.tutorialRuntime?.checkFirstEncounters?.(game.player, game.enemies, game.room?.walls);
  profilePerfSection(game, 'outline', () => game._updateEnemyTargetOutlines());
  game._handleSandboxHotkeys();
  // Keep HUD dash in realtime (not time-scaled) for UX clarity
  game.hud.update(game.player, game.roomIndex, game.totalRooms);
  game._updateExitPressureTimer(dt);

  game._updateEnemyStatusEffects(sdt);
  profilePerfSection(game, 'enemiesUpdate', () => {
    const playerX = game.player.x;
    const playerZ = game.player.z;
    for (const e of game.enemies) {
      if (!e?.alive) continue;
      const dx = e.x - playerX;
      const dz = e.z - playerZ;
      const far = (dx * dx + dz * dz) > ENEMY_FAR_LOD_DIST_SQ;
      e._lod = far ? 'far' : 'near';
      if (!Number.isFinite(e._lodDtCarry)) e._lodDtCarry = 0;

      if (far) {
        e._lodSkipFrame = !e._lodSkipFrame;
        if (e._lodSkipFrame) {
          e._lodDtCarry += sdt;
          continue;
        }
        const lodDt = sdt + e._lodDtCarry;
        e._lodDtCarry = 0;
        e.update(lodDt, game, { skipPresentation: true });
        continue;
      }

      e._lodSkipFrame = false;
      const lodDt = sdt + e._lodDtCarry;
      e._lodDtCarry = 0;
      e.update(lodDt, game);
    }
  });
  profilePerfSection(game, 'spawn', () => game.spawnRuntime?.update(sdt));
  game._updateExitPressureSpawns(dt);
  // Remove dead enemies next frame (keeps iteration safe)
  let anyAlive = false;
  for (let i = game.enemies.length - 1; i >= 0; i--) {
    const e = game.enemies[i];
    if (e.canDispose?.()) {
      e.dispose();
      game.enemies.splice(i, 1);
    } else anyAlive = true;
  }

  profilePerfSection(game, 'projectilesUpdate', () => game._updateProjectiles(sdt));
  game.slashFx.update(sdt, game.cameraRig?.camera);
  game.enemyDropPreview?.update(sdt);
  game._updateShards(sdt);
  game._updateAmmoPickups(sdt);
  game._updateWeaponPickups(sdt);
  game._updateGroundWeaponPickups(sdt);
  game._updateLootPickups(sdt);
  game._updatePlayerMines(sdt);
  game._updateDropTooltip();
  game._updatePlayerCursorLine(dt);

  game.cameraRig.setFollow(game.player.x, game.player.z);
  game.cameraRig.setLead(game.input.mouse.worldX, game.input.mouse.worldZ);
  game._updateAtmosphere(sdt);
  game._updateFogOfWar();
  profilePerfSection(game, 'minimap', () => game.minimap?.update(
    game.player?.position || game.player?.group?.position || game.player,
    game.player?.rotation?.y || game.player?.group?.rotation?.y || 0,
    game.spawnRuntime?.getActiveEnemies?.() || game.enemies,
    game.room?.getProps?.() || [],
  ));

  // Player reached door -> advance
  if (game.runMode !== 'sandbox' && game.room.doorOpen && game.room.playerAtDoor(game.player.x, game.player.z)) {
    game._advanceRoom();
  }

  return anyAlive;
}

export function updateGame(game, dt) {
  if (game.state === 'dying') {
    game._setPlayerCursorLineVisible(false);
    game._updateDeathSequence(dt);
    return;
  }
  if (game.state === 'paused' || game.state === 'menu' || game.state === 'gameover' || game.state === 'victory') {
    game._setPlayerCursorLineVisible(false);
    game.enemyDropPreview?.update(dt);
    game.hud.hideDropTooltip();
    game.hud.hidePickupPrompt?.();
    game._updateFogOfWar();
    return;
  }
  if (game.state === 'draft') {
    game._setPlayerCursorLineVisible(false);
    game.enemyDropPreview?.update(dt);
    game.hud.hideDropTooltip();
    game.hud.hidePickupPrompt?.();
    game._updateFogOfWar();
    return;
  }
  updatePlayingFrame(game, dt);
}


